// Vue publique en lecture seule de l'agenda — calendrier + liste + abonnement ICS
import { useCallback, useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { format, startOfMonth, endOfMonth, startOfWeek, endOfWeek, addDays, addMonths, isSameMonth, isToday } from "date-fns";
import { fr } from "date-fns/locale";
import { MapPin, Info, CalendarX, ChevronLeft, ChevronRight, CalendarPlus, Copy, Repeat } from "lucide-react";
import { CATEGORIES_AGENDA } from "@/lib/constants";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function PublicAgenda() {
  const { token } = useParams();
  const [data, setData] = useState(null);
  const [erreur, setErreur] = useState("");
  const [filtre, setFiltre] = useState("toutes");
  const [vue, setVue] = useState("calendrier");
  const [dateCourante, setDateCourante] = useState(new Date());
  const [jourSelectionne, setJourSelectionne] = useState(null);

  const charger = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/public/agenda/${token}`);
      setData(data);
    } catch (e) {
      setErreur(e?.response?.data?.detail || "Agenda indisponible.");
    }
  }, [token]);
  useEffect(() => { charger(); }, [charger]);

  const events = useMemo(() => {
    if (!data) return [];
    return filtre === "toutes" ? data.events : data.events.filter((e) => e.categorie === filtre);
  }, [data, filtre]);

  const jours = useMemo(() => {
    const debut = startOfWeek(startOfMonth(dateCourante), { weekStartsOn: 1 });
    const fin = endOfWeek(endOfMonth(dateCourante), { weekStartsOn: 1 });
    const liste = [];
    for (let d = debut; d <= fin; d = addDays(d, 1)) liste.push(d);
    return liste;
  }, [dateCourante]);

  const eventsDuJour = (jour) => events.filter((e) => e.date_debut?.slice(0, 10) === format(jour, "yyyy-MM-dd"));

  const urlIcs = filtre === "toutes"
    ? `${API}/public/agenda/${token}/calendrier.ics`
    : `${API}/public/agenda/${token}/calendrier.ics?categorie=${filtre}`;
  const urlWebcal = urlIcs.replace(/^https?:\/\//, "webcal://");

  if (erreur) {
    return (
      <div className="min-h-screen grid place-items-center bg-background px-6" data-testid="public-agenda-error">
        <div className="text-center max-w-md">
          <CalendarX className="h-10 w-10 mx-auto text-muted-foreground" />
          <h1 className="font-serif text-3xl font-bold mt-4">Lien indisponible</h1>
          <p className="text-muted-foreground text-sm mt-2">{erreur}</p>
        </div>
      </div>
    );
  }

  const listeAffichee = vue === "calendrier"
    ? (jourSelectionne
        ? eventsDuJour(jourSelectionne)
        : events.filter((e) => isSameMonth(new Date(e.date_debut), dateCourante)))
    : events.filter((e) => e.date_debut >= format(new Date(), "yyyy-MM-dd")).slice(0, 60);

  return (
    <div className="min-h-screen bg-background" data-testid="public-agenda-page">
      <header className="border-b border-border bg-card">
        <div className="max-w-5xl mx-auto px-6 py-10">
          <p className="text-xs uppercase tracking-[0.22em] text-muted-foreground">Agenda public</p>
          <h1 className="font-serif text-4xl sm:text-5xl font-bold mt-1">Inyange ASBL</h1>
          <p className="text-muted-foreground text-sm mt-2 max-w-xl">
            Répétitions, prestations, cours et rendez-vous ouverts. Les informations peuvent évoluer :
            reportez-vous toujours à la dernière version de cette page.
          </p>
        </div>
      </header>

      <main className="max-w-5xl mx-auto px-6 py-10 space-y-8">
        {/* Filtres + bascule de vue */}
        <div className="flex flex-wrap items-center gap-2.5">
          <div className="flex rounded-full bg-card border border-border p-1">
            {[["calendrier", "Calendrier"], ["liste", "Liste"]].map(([v, label]) => (
              <button
                key={v}
                onClick={() => { setVue(v); setJourSelectionne(null); }}
                className={`px-4 py-1 rounded-full text-sm font-medium transition-colors ${vue === v ? "bg-primary text-primary-foreground" : "text-muted-foreground"}`}
                data-testid={`public-view-${v}`}
              >
                {label}
              </button>
            ))}
          </div>
          <div className="flex flex-wrap gap-1.5" data-testid="public-agenda-filters">
            <button
              onClick={() => setFiltre("toutes")}
              className={`rounded-full px-4 py-1.5 text-xs font-medium border transition-colors ${filtre === "toutes" ? "bg-primary text-primary-foreground border-primary" : "border-border text-muted-foreground hover:bg-muted"}`}
              data-testid="public-filter-toutes"
            >
              Tout
            </button>
            {Object.entries(CATEGORIES_AGENDA).map(([k, v]) => (
              <button
                key={k}
                onClick={() => setFiltre(k)}
                className={`rounded-full px-4 py-1.5 text-xs font-medium border transition-colors ${filtre === k ? "text-white border-transparent" : "border-border text-muted-foreground hover:bg-muted"}`}
                style={filtre === k ? { background: v.couleur } : { borderLeft: `3px solid ${v.couleur}` }}
                data-testid={`public-filter-${k}`}
              >
                {v.label}
              </button>
            ))}
          </div>
        </div>

        {/* Calendrier */}
        {vue === "calendrier" && (
          <div className="space-y-3">
            <div className="flex items-center gap-2">
              <div className="flex items-center gap-1 rounded-full bg-card border border-border p-1">
                <button onClick={() => { setDateCourante(addMonths(dateCourante, -1)); setJourSelectionne(null); }} className="p-1.5 rounded-full hover:bg-muted transition-colors" data-testid="public-prev-month"><ChevronLeft className="h-4 w-4" /></button>
                <span className="px-3 text-sm font-semibold first-letter:capitalize min-w-[150px] text-center" data-testid="public-period-title">
                  {format(dateCourante, "MMMM yyyy", { locale: fr })}
                </span>
                <button onClick={() => { setDateCourante(addMonths(dateCourante, 1)); setJourSelectionne(null); }} className="p-1.5 rounded-full hover:bg-muted transition-colors" data-testid="public-next-month"><ChevronRight className="h-4 w-4" /></button>
              </div>
              {jourSelectionne && (
                <button onClick={() => setJourSelectionne(null)} className="text-xs text-muted-foreground hover:text-foreground transition-colors" data-testid="public-clear-day">
                  Voir tout le mois
                </button>
              )}
            </div>

            <div className="bg-card rounded-2xl border border-border overflow-hidden carte-douce" data-testid="public-calendar-grid">
              <div className="grid grid-cols-7 border-b border-border">
                {["Lun", "Mar", "Mer", "Jeu", "Ven", "Sam", "Dim"].map((j) => (
                  <div key={j} className="px-2 py-2.5 text-[11px] font-semibold uppercase tracking-wider text-muted-foreground text-center">{j}</div>
                ))}
              </div>
              <div className="grid grid-cols-7">
                {jours.map((jour) => {
                  const duJour = eventsDuJour(jour);
                  const horsMois = !isSameMonth(jour, dateCourante);
                  const actif = jourSelectionne && format(jour, "yyyy-MM-dd") === format(jourSelectionne, "yyyy-MM-dd");
                  return (
                    <button
                      key={jour.toISOString()}
                      onClick={() => setJourSelectionne(duJour.length ? jour : null)}
                      className={`text-left border-b border-r border-border/60 p-1.5 space-y-1 min-h-[92px] transition-colors ${horsMois ? "bg-muted/30" : ""} ${actif ? "ring-2 ring-inset ring-primary" : ""} ${duJour.length ? "hover:bg-muted/50 cursor-pointer" : "cursor-default"}`}
                      data-testid={`public-day-${format(jour, "yyyy-MM-dd")}`}
                    >
                      <span className={`text-xs font-semibold h-6 w-6 flex items-center justify-center rounded-full ${isToday(jour) ? "bg-primary text-primary-foreground" : horsMois ? "text-muted-foreground/50" : "text-muted-foreground"}`}>
                        {format(jour, "d")}
                      </span>
                      {duJour.slice(0, 3).map((e) => {
                        const cat = CATEGORIES_AGENDA[e.categorie];
                        return (
                          <span
                            key={e.id}
                            className="block rounded-md px-1.5 py-1 text-[11px] truncate"
                            style={{ background: `${cat?.couleur}1f`, borderLeft: `3px solid ${cat?.couleur}` }}
                          >
                            {format(new Date(e.date_debut), "HH'h'mm")} {e.titre}
                          </span>
                        );
                      })}
                      {duJour.length > 3 && (
                        <span className="block text-[10px] text-muted-foreground pl-1">+{duJour.length - 3} autre(s)</span>
                      )}
                    </button>
                  );
                })}
              </div>
            </div>
          </div>
        )}

        {/* Cartes détaillées */}
        <div className="space-y-5" data-testid="public-agenda-list">
          <h2 className="font-serif text-lg md:text-lg font-semibold">
            {vue === "liste"
              ? "Prochains événements"
              : jourSelectionne
                ? format(jourSelectionne, "EEEE d MMMM yyyy", { locale: fr })
                : `Événements du mois — ${format(dateCourante, "MMMM yyyy", { locale: fr })}`}
          </h2>
          {listeAffichee.map((e) => {
            const cat = CATEGORIES_AGENDA[e.categorie];
            return (
              <article key={e.id} className="bg-card rounded-2xl border border-border overflow-hidden carte-douce" data-testid={`public-event-${e.id}`}>
                {e.a_affiche && (
                  <img
                    src={`${API}/events/${e.serie_id || e.id}/image`}
                    alt={`Affiche — ${e.titre}`}
                    className="w-full max-h-96 object-contain bg-muted"
                    data-testid={`public-event-image-${e.id}`}
                  />
                )}
                <div className="p-6">
                  <div className="flex flex-wrap items-center gap-2">
                    <span className="rounded-full px-3 py-1 text-[11px] font-semibold text-white" style={{ background: cat?.couleur }}>
                      {cat?.label}
                    </span>
                    <span className="text-xs text-muted-foreground first-letter:capitalize">
                      {format(new Date(e.date_debut), "EEEE d MMMM yyyy 'à' HH'h'mm", { locale: fr })}
                      {e.date_fin && ` → ${format(new Date(e.date_fin), "HH'h'mm", { locale: fr })}`}
                    </span>
                    {e.recurrence_frequence && e.recurrence_frequence !== "aucune" && (
                      <span className="text-[11px] text-muted-foreground inline-flex items-center gap-1">
                        <Repeat className="h-3 w-3" /> récurrent
                      </span>
                    )}
                  </div>
                  <h3 className="font-serif text-2xl font-semibold mt-3">{e.titre}</h3>
                  {e.lieu && (
                    <p className="text-sm text-muted-foreground mt-1 inline-flex items-center gap-1.5">
                      <MapPin className="h-3.5 w-3.5" /> {e.lieu}
                    </p>
                  )}
                  {e.description_html && (
                    <div className="prose-inyange mt-4 text-sm" dangerouslySetInnerHTML={{ __html: e.description_html }} />
                  )}
                  {e.infos_pratiques && (
                    <div className="mt-4 rounded-xl bg-accent/15 border border-accent/40 px-4 py-3">
                      <p className="text-xs font-semibold uppercase tracking-wide inline-flex items-center gap-1.5">
                        <Info className="h-3.5 w-3.5" /> Informations importantes
                      </p>
                      <p className="text-sm mt-1 whitespace-pre-line">{e.infos_pratiques}</p>
                    </div>
                  )}
                </div>
              </article>
            );
          })}
          {data && listeAffichee.length === 0 && (
            <p className="text-sm text-muted-foreground text-center py-12" data-testid="public-agenda-empty">
              Aucun événement sur cette période.
            </p>
          )}
        </div>

        {/* Abonnement calendrier */}
        <div className="rounded-2xl border border-border bg-card p-6 carte-douce" data-testid="public-subscribe-block">
          <h2 className="font-serif text-xl font-semibold inline-flex items-center gap-2">
            <CalendarPlus className="h-5 w-5 text-primary" /> Ajouter cet agenda à votre calendrier
          </h2>
          <p className="text-sm text-muted-foreground mt-1.5">
            Abonnez-vous une fois : les nouveaux événements et les modifications arrivent automatiquement dans
            Google Agenda, Apple Calendar ou Outlook.
            {filtre !== "toutes" && ` Vous vous abonnez ici uniquement à « ${CATEGORIES_AGENDA[filtre].label} ».`}
          </p>
          <div className="flex flex-wrap items-center gap-2.5 mt-4">
            <a
              href={urlWebcal}
              className="rounded-full bg-primary text-primary-foreground text-sm font-medium px-5 py-2.5 hover:opacity-90 transition-opacity"
              data-testid="public-subscribe-webcal"
            >
              S'abonner (Apple, Outlook)
            </a>
            <a
              href={`https://calendar.google.com/calendar/r?cid=${encodeURIComponent(urlIcs)}`}
              target="_blank" rel="noopener noreferrer"
              className="rounded-full border border-border text-sm font-medium px-5 py-2.5 hover:bg-muted transition-colors"
              data-testid="public-subscribe-google"
            >
              Ajouter à Google Agenda
            </a>
            <button
              onClick={() => navigator.clipboard.writeText(urlIcs)}
              className="rounded-full border border-border text-sm font-medium px-5 py-2.5 hover:bg-muted transition-colors inline-flex items-center gap-1.5"
              data-testid="public-subscribe-copy"
            >
              <Copy className="h-3.5 w-3.5" /> Copier l'adresse du flux
            </button>
            <a
              href={urlIcs}
              className="text-sm text-muted-foreground hover:text-foreground transition-colors underline"
              data-testid="public-subscribe-download"
            >
              Télécharger le fichier .ics
            </a>
          </div>
        </div>
      </main>

      <footer className="border-t border-border mt-10">
        <div className="max-w-5xl mx-auto px-6 py-8 text-xs text-muted-foreground">
          Inyange ASBL — agenda partagé en lecture seule.
        </div>
      </footer>
    </div>
  );
}
